import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Code, ExternalLink, Zap, ShoppingCart } from 'lucide-react';

const ProductCard = ({ product, featured = false, viewMode = 'grid' }) => {
  if (viewMode === 'list') {
    return (
      <Link 
        to={`/product/${product.id}`}
        className={`glass-card p-6 rounded-2xl card-hover group flex flex-col md:flex-row gap-6 ${featured ? 'border-2 border-cyan-500/50' : 'border border-cyan-500/10'}`}
        data-testid={`product-card-${product.id}`}
      >
        {/* Image */}
        <div className="relative rounded-xl overflow-hidden md:w-64 h-48 bg-gradient-to-br from-gray-800 to-gray-900 flex-shrink-0">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Code size={48} className="text-gray-600" />
            </div>
          )}
          {featured && (
            <div className="absolute top-3 left-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center space-x-1">
              <Zap size={12} />
              <span>Featured</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-2xl font-bold text-white group-hover:text-cyan-400 transition">
                {product.name}
              </h3>
              <p className="text-gray-400 text-sm mt-2 line-clamp-2">
                {product.description}
              </p>
            </div>
            <div className="glass-card px-4 py-2 rounded-lg text-lg font-bold text-cyan-400 ml-4">
              ₹{product.price}
            </div>
          </div>

          {/* Tech Stack */}
          {product.tech_stack && product.tech_stack.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {product.tech_stack.slice(0, 5).map((tech, index) => (
                <span key={index} className="px-3 py-1 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 rounded-lg text-xs text-cyan-400 font-semibold">
                  {tech}
                </span>
              ))}
              {product.tech_stack.length > 5 && (
                <span className="px-3 py-1 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                  +{product.tech_stack.length - 5}
                </span>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-800/50">
            <div className="flex items-center space-x-1 text-yellow-400">
              <Star size={18} fill="currentColor" />
              <span className="text-sm font-semibold">{product.rating || '5.0'}</span>
              <span className="text-gray-500 text-xs ml-1">(50+ reviews)</span>
            </div>
            <div className="flex items-center text-cyan-400 text-sm font-semibold group-hover:translate-x-1 transition">
              View Details <ExternalLink size={14} className="ml-1" />
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link 
      to={`/product/${product.id}`}
      className={`glass-card rounded-2xl overflow-hidden card-hover group relative ${
        featured ? 'border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/20' : 'border border-cyan-500/10'
      }`}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-56 bg-gradient-to-br from-gray-800 to-gray-900">
        {product.image_url ? (
          <img 
            src={product.image_url} 
            alt={product.name} 
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Code size={56} className="text-gray-600" />
          </div>
        )}
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {featured && (
          <div className="absolute top-4 left-4 bg-gradient-to-r from-cyan-500 to-teal-500 text-white px-4 py-1.5 rounded-full text-xs font-bold flex items-center space-x-1 shadow-lg">
            <Zap size={14} />
            <span>Featured</span>
          </div>
        )}
        <div className="absolute top-4 right-4 glass-card px-4 py-2 rounded-xl text-lg font-bold backdrop-blur-md">
          <span className="text-cyan-400">₹{product.price}</span>
        </div>

        {/* Quick Action Button */}
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
          <div className="p-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl shadow-lg">
            <ShoppingCart size={20} className="text-gray-900" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition mb-2">
            {product.name}
          </h3>
          <p className="text-gray-400 text-sm line-clamp-2 leading-relaxed">
            {product.description}
          </p>
        </div>

        {/* Tech Stack */}
        {product.tech_stack && product.tech_stack.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {product.tech_stack.slice(0, 3).map((tech, index) => (
              <span 
                key={index} 
                className="px-3 py-1 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 rounded-lg text-xs text-cyan-400 font-semibold"
              >
                {tech}
              </span>
            ))}
            {product.tech_stack.length > 3 && (
              <span className="px-3 py-1 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                +{product.tech_stack.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-800/50">
          <div className="flex items-center space-x-1.5">
            <div className="flex items-center space-x-0.5 text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} fill="currentColor" />
              ))}
            </div>
            <span className="text-sm font-semibold text-white">{product.rating || '5.0'}</span>
          </div>
          <div className="flex items-center text-cyan-400 text-sm font-semibold group-hover:translate-x-1 transition">
            <span>View</span>
            <ExternalLink size={14} className="ml-1" />
          </div>
        </div>
      </div>

      {/* Hover Border Glow */}
      <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-cyan-500/30 transition-colors duration-300 pointer-events-none"></div>
    </Link>
  );
};

export default ProductCard;